<template v-if="todisplay">
    <form v-show="isvisible" enctype="multipart/form-data" novalidate :class="classes">
        <QFile ref="fileInput"
               class="input-file"
               type="file"
               v-model="files"
               outlined
               dense
               use-chips
               bottom-slots
               hide-bottom-space
               :persistent-hint="!!tooltip || successes.length > 0"
               :label="Translate(labelText)"
               :placeholder="Translate(labelText)"
               :loading="isSaving"
               :multiple="multiple"
               :disable="isSaving"
               :accept="aceptTypes"
               :error="errors.length > 0 ? true : null"
               @update:model-value="filesChange">
            
            <template #prepend>
                <QIcon
                    name="mdi-cloud-upload-outline"
                    class="cursor-pointer"
                    @click="openFilePicker"
                />
            </template>
            
            <template #selected-item="{ index, file }">
                <QChip
                    v-if="index < 2"
                    size="sm"
                    color="deep-purple-7"
                    text-color="white"
                    class="input-file__chip"
                >
                    <QTooltip v-if="file.name && file.name.length >= truncateLength">{{ file.name }}</QTooltip>
                    {{ truncateFileName(file.name) }}
                </QChip>
                <span v-else-if="index === 2" class="text-caption text-grey-7 q-ml-xs">
                    +{{ fileCount - 2 }} File(s)
                </span>
            </template>

            <template #append v-if="fileCount > 0">
                <QBtn flat round dense @click.stop="doUpload">
                    <QTooltip>Upload Now</QTooltip>
                    <QIcon class="blink-text text-pink" name="mdi-folder-upload-outline"></QIcon>
                </QBtn>
            </template>

            <template v-if="tooltip || successes.length" #hint>
                <div class="text-left">
                    <div v-if="tooltip">{{ tooltip }}</div>
                    <div v-if="successes.length" class="text-positive q-mt-xs">
                        <div v-for="(success, index) in successes" :key="`ok-${index}`">{{ success }}</div>
                    </div>
                </div>
            </template>

            <template v-if="errors.length" #error>
                <div class="text-left">
                    <div v-for="(error, index) in errors" :key="`err-${index}`">{{ error }}</div>
                </div>
            </template>
        </QFile>

    </form>
</template>

<script>
//import Vue from 'vue';
import { QFile, QChip, QBtn, QTooltip, QIcon } from 'quasar';
import utils from '../../Shared/utils.jsx';
import BaseComponentMixin from './BaseComponentMixin.jsx';
import api from '@/Services/api';

    // Match Vuetify v-file-input default truncate-length behavior.
    const DEFAULT_TRUNCATE_LENGTH = 22;

    export default {
        name: 'BasicFileUpload',
        components: {
            QFile,
            QChip,
            QBtn,
            QTooltip,
            QIcon,
        },
        mixins: [BaseComponentMixin],
        data: function () {
            return {
                files: [],
                finalUploadURL: '',
                queryString: '',

                isSaving: false,
                successes: [],
                errors: [],

                uploadURLexpn: null,
                uploadPathexpn: null,
                aceptTypesexpn: null,
                labelText: 'To upload, click to browse',
                truncateLength: DEFAULT_TRUNCATE_LENGTH,

                // Measured once in mounted(): is it layout-safe for this control to grow taller
                // when a hint/error/success shows? See reserveBottomSpace().
                parentTopAlignsChildren: false,
            }
        },
        computed: {
            classes() {
                return {
                    [`c-${this.type}`]: true,
                    [`c-name-${this.name || 'unnamed'}`]: true,
                    // Match BasicButton horizontal gutter (q-mr-sm) so row spacing is even
                    // next to New folder / Delete; avoid q-ma-xs which added uneven 4px sides.
                    'q-mr-sm': true,
                    'c-BasicFileUpload--with-bottom': this.reserveBottomSpace,
                };
            },
            styles() {
                return {
                    ...utils.resolveStyleHints(this.styleHints, this),
                    ...this.sizeStyle,
                }
            },
            uploadURL() {
                if (!this.uploadURLexpn && this.controlData.UploadURL)
                    this.uploadURLexpn = utils.compile(this, this.controlData.UploadURL);

                return this.uploadURLexpn ? utils.evaluate(this.uploadURLexpn, this) : '';
            },
            uploadPath() {
                if (!this.uploadPathexpn && this.controlData.UploadPath)
                    this.uploadPathexpn = utils.compile(this, this.controlData.UploadPath);

                return this.uploadPathexpn ? utils.evaluate(this.uploadPathexpn, this) : '';
            },
            aceptTypes() {
                if (!this.aceptTypesexpn && this.controlData.AcceptTypes)
                    this.aceptTypesexpn = utils.compile(this, this.controlData.AcceptTypes);

                return this.aceptTypesexpn ? utils.evaluate(this.aceptTypesexpn, this) : '';
            },
            multiple() {
                return this.controlData.AllowMultiple;
            },
            fileCount() {
                if (Array.isArray(this.files))
                    return this.files.length;
                return this.files ? 1 : 0;
            },
            tooltip() {
                if (this.controlData.Tooltip)
                    return this.controlData.Tooltip;
                return '';
            },
            /**
             * Whether growing the form's box (to reserve room for the hint/error below the
             * control) is layout-safe, independent of Tooltip/success/error content.
             *
             * The hint itself is always position:absolute (see <style>), so it never affects
             * height on its own. The risk is the form's OWN padding-bottom, which grows the
             * flex item that the parent HorizontalLayout row sizes against:
             *   - EditChat Experience: this control is the ONLY item in its row, and that row
             *     top-aligns (align-items: flex-start, the HorizontalLayout default). Growing
             *     taller just pushes the next stacked field down — safe, and exactly what a
             *     normal Quasar field with a hint already does (see Header Text / API Key).
             *   - BrowseFiles toolbar: this control shares its row with New folder / Delete,
             *     and that row is align-items: center. Growing taller recenters the row,
             *     shifting the control up relative to those fixed-height buttons — unsafe,
             *     regardless of whether a Tooltip is configured.
             * parentTopAlignsChildren is measured once from the live DOM in mounted() so this
             * does not depend on a per-document schema flag or guessing from controlData.
             */
            reserveBottomSpace() {
                return this.parentTopAlignsChildren;
            },
        },
        created() {
            if (this.controlData.Label) {
                this.labelText = this.controlData.Label;
            }
        },
        mounted() {
            // Default (parentTopAlignsChildren: false) is the safe choice — never grow — so an
            // unmeasurable parent (e.g. todisplay false at mount) falls back to the old
            // always-fixed-height toolbar behavior rather than risking a misaligned toolbar.
            const row = this.$el?.parentElement;
            if (row) {
                const alignItems = getComputedStyle(row).alignItems;
                this.parentTopAlignsChildren = alignItems === 'flex-start' || alignItems === 'start' || alignItems === 'normal';
            }
        },
        methods: {
            preRenderComplete() {
                this.finishRenderHandler(this);
            },
            // Same middle-ellipsis algorithm as Vuetify's v-file-input truncateText.
            truncateFileName(name) {
                if (!name || name.length < this.truncateLength)
                    return name || '';
                const charsKeepOneSide = Math.floor((this.truncateLength - 1) / 2);
                return `${name.slice(0, charsKeepOneSide)}…${name.slice(name.length - charsKeepOneSide)}`;
            },
            async filesChange(fileList) {
                this.errors = [];
                this.successes = [];

                this.files = [];
                
                if (!fileList) return;
                if (Array.isArray(fileList) && !fileList.length) return;

                if (Array.isArray(fileList))
                    this.files = fileList; //list of files selected.
                else
                    this.files.push(fileList); //fileList is the single file that was selected.
                

                this.constructUploadURL();

                if (!this.controlData.UploadFilesAutomatically)
                    return;

                await this.uploadFileList(this.files);
            },
            constructUploadURL() {
                let finalURL = this.uploadURL || "/Apps/FileBinary2";

                if(finalURL == "/Apps/FileBinary2")
                {
                    this.finalUploadURL = finalURL;
                    this.queryString = "?FullNameWithPath=";
                    
                    if (this.uploadPath) {
                        if (this.uploadPath.substr(0, 1) == '/') // skip the leading / if exists
                            this.queryString += this.uploadPath.substr(1, this.uploadPath.length);
                        else
                            this.queryString += this.uploadPath;

                        if (this.uploadPath.substr(this.uploadPath.length - 1, 1) != '/') // if the uploadPath doesn't end with / append it
                            this.queryString += "/";
                    }
                } else {
                    if (finalURL.substr(finalURL.length - 1, 1) != '/') // if the url doesn't end with / append it
                        finalURL += "/";

                    if (this.uploadPath) {
                        if (this.uploadPath.substr(0, 1) == '/') // skip the leading / if exists
                            finalURL += this.uploadPath.substr(1, this.uploadPath.length);
                        else
                            finalURL += this.uploadPath;

                        if (finalURL.substr(finalURL.length - 1, 1) != '/') // if the url doesn't end with / append it
                            finalURL += "/";
                    }


                    this.finalUploadURL = finalURL;
                }
                
                utils.debug(`finalUploadURL:${this.finalUploadURL}    querystring:${this.queryString}`);
            },
            readFileAsync(file) {
                const c = this;

                return new Promise((resolve, reject) => {
                    var reader = new FileReader();
                    reader.onload = function (event) {
                        file.data = event.target.result; // event.target.result varies depending on which method was called on the reader (readAsDataURL || readAsArrayBuffer || readAsText) 

                        utils.executeAndCompileAllActions(c.controlData.FileUploadActions, file, c);

                        resolve(file);
                    };

                    reader.onerror = reject;

                    if (this.controlData.ActionType == 'DataURL')
                        reader.readAsDataURL(file);
                    else if (this.controlData.ActionType == 'BinaryData')
                        reader.readAsArrayBuffer(file);
                    else
                        reader.readAsText(file);
                });
            },
            async uploadFileList(fileList) {
                this.isSaving = true;
                try {
                    const res = fileList.map(f => this.uploadFile(f));
                    await Promise.all(res);

                    this.files = [];
                    this.errors = [];
                    this.successes.push(this.Translate(`Uploaded {{COUNT}} file(s) successfully`).replace('{{COUNT}}', `${fileList.length}`));

                    await utils.executeAndCompileAllActions(this.controlData.Actions, fileList, this);
                }
                catch (e) {
                    utils.warn('uploadFileList failed', e);
                    this.errors.push(this.Translate(e.statusText));
                    await utils.executeAndCompileAllActions(this.controlData.FailureActions, e, this);
                }
                finally {
                    this.isSaving = false;
                }
                return;
            },
            async uploadFile(file) {
                if (this.controlData.ActionType == 'DataURL' || this.controlData.ActionType == 'BinaryData' || this.controlData.ActionType == 'TextData') {
                    return await this.readFileAsync(file);
                }
                else // c.controlData.ActionType == 'UploadURL'
                {
                    var data = new FormData(); //FILL FormData WITH FILE DETAILS.

                    data.append("uploadedFile", file);
                    var fileName = encodeURIComponent(file.name);

                    try {
                        const response = await api.apiRequest({
                            method: 'POST',
                            data: data,
                            url: this.finalUploadURL + this.queryString + fileName,
                            doNotUseWebsocket: true,
                            multipart: true // required to set the headers properly to allow multi-part data to be uploaded
                        });                    

                        if (!this.controlData.SuppressUploadToast)
                            ;//toastr.success('', 'File ' + file.name + ' uploaded successfully.');

                        if (response.data && response.data.Result)
                            file.data = response.data.Result;
                        else if (response.data)
                            file.data = response.data;

                        await utils.executeAndCompileAllActions(this.controlData.FileUploadActions, file, this);

                        return file;
                    }
                    catch (error) {
                        await utils.executeAndCompileAllActions(this.controlData.FileUploadFailureActions, { error, file }, this);

                        throw (error);
                    };
                }
            },
            doUpload() {
                this.uploadFileList(this.files);
            },
            openFilePicker(e) {
                if (this.isSaving)
                    return;
                // Prepend icon sits outside QFile's native click target; mirror input clicks.
                this.$refs.fileInput?.pickFiles?.(e);
            },
        },
        props: {
        },
    };
</script>

<style xlang="scss" scoped>
    /* Hints/errors are absolute under the QFile so they never affect control height on their
       own. form.c-BasicFileUpload--with-bottom (padding-bottom) reserves flow space for them —
       only applied when mounted() measures the parent row as top-aligned (see
       reserveBottomSpace() for why: growing taller is unsafe in an align-items:center toolbar
       row like BrowseFiles' New folder / Delete). */
    form {
        position: relative;
        overflow: visible;
    }

    form.c-BasicFileUpload--with-bottom {
        padding-bottom: 20px;
    }

    .input-file {
        min-width: 280px;
        max-width: 360px;
    }

    .input-file :deep(.q-field__bottom) {
        position: absolute !important;
        left: 0;
        right: 0;
        top: 100%;
        transform: none !important;
        z-index: 1;
        pointer-events: none;
    }

    .input-file__chip {
        max-width: 100%;
    }

    .blink-text {
        animation: blink 1s linear infinite;
    }

    @keyframes blink {
        0% {
            opacity: .33;
        }
        50% {
            opacity: .66;
        }
        100% {
            opacity: 1;
        }
    }
</style>
